'use client';

import { AlertTriangle, AudioLines, CircleHelp, MessageSquareText, Video } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { TurnEmotionCue, TurnEmotionEvent, TurnEmotionLabel } from '@/types/turn-emotion';

const labels: Record<TurnEmotionLabel, string> = {
  angry: '愤怒',
  disgusted: '厌恶',
  fearful: '紧张或恐惧',
  happy: '积极或愉快',
  neutral: '中性或平稳',
  sad: '低落',
  surprised: '惊讶',
};

function Cue({ cue }: { cue: TurnEmotionCue }) {
  const Icon = cue.modality === 'audio' ? AudioLines : Video;
  return (
    <div className="flex items-center gap-2 text-sm text-slate-700">
      <Icon className="h-4 w-4 text-blue-500" />
      <span>{cue.modality === 'audio' ? '声音语调' : '面部表情'}：</span>
      <span>可能偏{labels[cue.label]}</span>
    </div>
  );
}

function ResultBody({ event }: { event: TurnEmotionEvent }) {
  if (event.status === 'skipped') {
    return (
      <p className="flex items-center gap-2 text-sm text-slate-600">
        <MessageSquareText className="h-4 w-4" />
        文字输入，本轮未采集对应音视频。
      </p>
    );
  }
  if (event.status === 'unavailable') {
    return (
      <p className="flex items-center gap-2 text-sm text-amber-700">
        <AlertTriangle className="h-4 w-4" />
        情绪服务本轮不可用，对话已正常降级。
      </p>
    );
  }
  if (event.status === 'insufficient' || event.relation === 'insufficient') {
    return (
      <p className="flex items-center gap-2 text-sm text-slate-600">
        <CircleHelp className="h-4 w-4" />
        本轮音视频数据不足，模型已弃权。
      </p>
    );
  }
  if (event.relation === 'conflict_with_text') {
    return <p className="text-sm text-amber-700">与用户明确自述冲突，本轮忽略模型标签。</p>;
  }
  return (
    <div className="space-y-2">
      {event.cues.map((cue) => (
        <Cue key={`${cue.modality}-${cue.label}`} cue={cue} />
      ))}
      {event.relation === 'agree' && (
        <p className="text-xs text-emerald-700">音视频线索一致，但仍只作为弱提示。</p>
      )}
      {event.relation === 'conflict' && (
        <p className="text-xs text-amber-700">模态线索冲突，不合并为确定结论。</p>
      )}
      {event.relation === 'single_modality' && (
        <p className="text-xs text-slate-500">仅一个模态可用，不外推其他模态。</p>
      )}
    </div>
  );
}

function EventCard({ event, latest = false }: { event: TurnEmotionEvent; latest?: boolean }) {
  return (
    <article
      className={cn(
        'rounded-xl border bg-white p-4 shadow-sm',
        latest ? 'border-blue-200' : 'border-slate-200'
      )}
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <Badge variant={event.status === 'ready' ? 'default' : 'secondary'}>
          {latest ? '最新患者 Turn' : '患者 Turn'}
        </Badge>
        {event.service_latency_ms !== null && (
          <span className="text-xs text-slate-400">模型 {event.service_latency_ms} ms</span>
        )}
      </div>
      <ResultBody event={event} />
    </article>
  );
}

export function TurnEmotionPanel({
  latest,
  history,
  className,
}: {
  latest?: TurnEmotionEvent;
  history: TurnEmotionEvent[];
  className?: string;
}) {
  return (
    <aside className={cn('h-full w-[380px] overflow-y-auto bg-slate-50 p-5', className)}>
      <div className="mb-5">
        <h2 className="text-lg font-semibold text-slate-900">Turn 情绪辅助</h2>
        <p className="mt-1 text-xs leading-5 text-slate-500">
          实验性弱线索，不代表真实情绪或诊断。
        </p>
      </div>
      {!latest ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
          等待患者完成一个 Turn 后显示结果。
        </div>
      ) : (
        <div className="space-y-4">
          <EventCard event={latest} latest />
          {history.length > 1 && (
            <div className="space-y-3">
              <h3 className="text-xs font-medium tracking-wide text-slate-500 uppercase">
                最近记录（最多10轮）
              </h3>
              {history.slice(1).map((event) => (
                <EventCard key={`${event.turn_id}-${event.message_id}`} event={event} />
              ))}
            </div>
          )}
        </div>
      )}
    </aside>
  );
}
